import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CheckCircle2, Circle, Trash2, CalendarClock } from "lucide-react";
import { PageHeader, Panel, Badge, Select, fmtD } from "@/components/admin/ui";

const empty = { title: "", description: "", assigned_to: "", due_date: "", priority: "normal" };

export default function StoreTasks() {
  const { sid } = useParams();
  const { user } = useAuth();
  const canManage = ["super_admin", "admin", "manager"].includes(user?.role) || user?.permissions?.tasks === "manage";
  const [tasks, setTasks] = useState([]);
  const [emps, setEmps] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);

  const load = useCallback(() => {
    api.get(`/stores/${sid}/tasks${filter ? `?status=${filter}` : ""}`).then((r) => setTasks(r.data)).catch(() => setTasks([]));
  }, [sid, filter]);
  useEffect(load, [load]);
  useEffect(() => { if (canManage) api.get(`/employees?store_id=${sid}`).then((r) => setEmps(r.data)).catch(() => {}); }, [sid, canManage]);

  const create = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    try {
      await api.post(`/stores/${sid}/tasks`, { ...f, assigned_to: f.assigned_to || null, due_date: f.due_date || null });
      toast.success("Task created"); setOpen(false); setF(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (t) => {
    try { await api.patch(`/tasks/${t.id}`, { status: t.status === "open" ? "done" : "open" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm("Delete task?")) return;
    try { await api.delete(`/tasks/${t.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Store · Team" title={canManage ? "Tasks" : "My Tasks"} testId="tasks-title"
        subtitle={`${tasks.filter((t) => t.status === "open").length} open · ${tasks.filter((t) => t.overdue).length} overdue`}
        actions={<>
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="tasks-filter"><option value="open">Open</option><option value="done">Done</option><option value="">All</option></Select>
          {canManage && <Button onClick={() => setOpen(true)} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="task-new"><Plus className="w-4 h-4 mr-1"/>Assign Task</Button>}
        </>}
      />
      <Panel className="divide-y divide-neutral-100">
        {tasks.length === 0 && <p className="p-10 text-center text-sm text-neutral-500" data-testid="tasks-empty">No tasks here. Nice work!</p>}
        {tasks.map((t) => (
          <div key={t.id} className="p-4 flex items-center gap-3" data-testid={`task-${t.id}`}>
            <button onClick={() => toggle(t)} className="shrink-0" data-testid={`task-toggle-${t.id}`}>
              {t.status === "done" ? <CheckCircle2 className="w-7 h-7 text-emerald-500"/> : <Circle className="w-7 h-7 text-neutral-300 hover:text-neutral-500"/>}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${t.status === "done" ? "line-through text-neutral-400" : ""}`}>{t.title}</div>
              <div className="text-xs text-neutral-500 flex flex-wrap gap-x-3 mt-0.5">
                {t.assigned_to_name ? <span>→ {t.assigned_to_name}</span> : <span>Anyone</span>}
                {t.due_date && <span className={t.overdue ? "text-red-600 font-semibold" : ""}><CalendarClock className="w-3 h-3 inline mr-0.5"/>{fmtD(t.due_date)}{t.overdue ? " · overdue" : ""}</span>}
                {t.description && <span className="truncate">{t.description}</span>}
              </div>
            </div>
            <Badge>{t.priority}</Badge>
            {canManage && <button onClick={() => remove(t)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>}
          </div>
        ))}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input"/></div>
            <div><Label>Details</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assign to</Label><Select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className="w-full" data-testid="task-assignee"><option value="">Anyone at this store</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select></div>
              <div><Label>Due date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due"/></div>
            </div>
            <div><Label>Priority</Label><Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="w-full"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></Select></div>
            <Button onClick={create} className="w-full rounded-xl bg-[hsl(var(--primary))]" data-testid="task-save">Create Task</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
